import { Fetchable } from '@astlai/common/util/fetchable';
import type { Badge } from '$lib/SourceBadge.svelte';
import { resourcesClient } from '$lib/resources-client';
import type { DocumentDetail, DocumentOverview, QuotedWebSource } from 'src/types/llm';
import type { InlineQuote } from 'src/util/citation-transformer';
import { getContext, setContext } from 'svelte';

export type SelectedItem = {
	document?: DocumentDetail;
	source?: QuotedWebSource;
	badge?: Badge;
	quotes?: InlineQuote[];
};

export class DocumentContext {
	private fetchedDocument: DocumentDetail | null = $state(null);

	documents: DocumentOverview[] = $state([]);
	webSources: QuotedWebSource[] = $state([]);
	badges: Record<number, Badge> = $state({});
	quotes: Record<number, InlineQuote[]> = $state({});

	selectedDocId: number | null = $state(null);
	selectedRefId: string | null = $state(null);
	selectedSecIdx: string | null = $state(null);

	readonly fetchable = new Fetchable();

	readonly selected: SelectedItem | null = $derived.by(() => {
		if (this.selectedSecIdx != null) {
			const source = this.webSources.find((s) => s.id === this.selectedSecIdx);
			return source ? { source } : null;
		}
		if (this.fetchedDocument != null) {
			return {
				document: this.fetchedDocument,
				badge: this.badges[this.fetchedDocument.id],
				quotes: this.quotes[this.fetchedDocument.id]
			};
		}
		return null;
	});

	constructor() {
		$effect(() => {
			if (this.selectedDocId != null) {
				this.fetchable.fetch(async () => {
					this.fetchedDocument = await resourcesClient.getDocument(this.selectedDocId);
				});
			} else {
				this.fetchedDocument = null;
			}
		});
	}

	selectDocument(docId: number, refId?: string | null) {
		this.selectedDocId = docId;
		this.selectedRefId = refId ?? null;
		this.selectedSecIdx = null;
	}

	selectWebSource(webSourceId: string) {
		this.selectedSecIdx = webSourceId;
		this.selectedDocId = null;
		this.selectedRefId = null;
	}

	clearSelection() {
		this.selectedDocId = null;
		this.selectedRefId = null;
		this.selectedSecIdx = null;
	}
}

export const DOCUMENTS_CONTEXT_KEY = Symbol('documents');

export function getDocumentContext() {
	return getContext<DocumentContext>(DOCUMENTS_CONTEXT_KEY);
}

export function setDocumentContext(ctx: DocumentContext) {
	setContext(DOCUMENTS_CONTEXT_KEY, ctx);
	return ctx;
}
